/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

import { flexRender, type Header } from '@tanstack/react-table';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip, {
	TooltipPrimitive,
	type TooltipPrimitiveProps,
	type TooltipProps,
} from '@atlaskit/tooltip';

const styles = cssMap({
	headerDescription: {
		boxSizing: 'border-box',
		maxHeight: '500px',
		maxWidth: '300px',
		boxShadow: token('elevation.shadow.overlay'),
		color: token('color.text'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		borderRadius: token('radius.xsmall'),
		marginTop: token('space.050'),
		marginRight: token('space.050'),
		marginBottom: token('space.050'),
		marginLeft: token('space.050'),
	},
});

interface HeaderTooltipProps<T extends object> {
	charlieTableColumn: Header<T, unknown>;
	children: TooltipProps['children'];
}

/**
 * Tooltip wrapper for table header cells. Shows column header and meta description when present.
 */
export const HeaderTooltip = <T extends object>({
	charlieTableColumn,
	children,
}: HeaderTooltipProps<T>): JSX.Element => {
	const description = charlieTableColumn.column.columnDef.meta?.description;
	const hasDescription = Boolean(description);

	return (
		<Tooltip
			component={forwardRef<HTMLDivElement, TooltipPrimitiveProps>((props, ref) => {
				const { children: tooltipChildren, ...rest } = props;
				if (ref == null) {
					return null;
				}
				return (
					<TooltipPrimitive {...rest} ref={ref}>
						<Box backgroundColor="elevation.surface.overlay" xcss={styles.headerDescription}>
							{tooltipChildren}
						</Box>
					</TooltipPrimitive>
				);
			})}
			content={
				hasDescription && (
					<Box>
						<b>
							{flexRender(
								charlieTableColumn.column.columnDef.header,
								charlieTableColumn.getContext(),
							)}
						</b>
						{description}
					</Box>
				)
			}
			position="bottom"
		>
			{children}
		</Tooltip>
	);
};
